import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { ApiError, api, type AuthUser } from "../lib/api";
import { useAuthStatus, useMe } from "../App";
import { AppFooter } from "../components/AppFooter";
import { AppHeader } from "../components/AppHeader";
import { Spinner } from "../components/ui";

export default function SetupPage() {
  const status = useAuthStatus();
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const setup = useMutation({
    mutationFn: () => api.post<{ user: AuthUser }>("/api/auth/setup", { username, email, password }),
    onSuccess: (res) => {
      queryClient.setQueryData(["me"], res.user);
      queryClient.invalidateQueries({ queryKey: ["auth-status"] });
      navigate("/", { replace: true });
    },
  });

  if (me.data) return <Navigate to="/" replace />;
  if (status.data && !status.data.needsSetup) return <Navigate to="/login" replace />;

  const error = localError ?? (setup.error instanceof ApiError ? setup.error.message : null);

  function submit() {
    setLocalError(null);
    if (username.trim().length < 3) {
      setLocalError("Username minimal 3 karakter");
      return;
    }
    if (password.length < 8) {
      setLocalError("Password minimal 8 karakter");
      return;
    }
    if (password !== confirm) {
      setLocalError("Konfirmasi password tidak cocok");
      return;
    }
    setup.mutate();
  }

  return (
    <div className="flex min-h-screen flex-col">
      <AppHeader />
      <main className="flex flex-1 items-center justify-center px-4 py-6">
        <div className="card w-full max-w-sm p-6">
          <div className="mb-6 flex items-center gap-2">
            <span className="flex size-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <BarChart3 size={18} />
            </span>
            <div>
              <h1 className="text-base font-semibold text-slate-900">Selamat datang</h1>
              <p className="text-xs text-slate-500">Buat akun admin untuk memulai</p>
            </div>
          </div>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <div>
              <label className="label" htmlFor="username">
                Username
              </label>
              <input
                id="username"
                className="input"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                minLength={3}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                className="input"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="confirm">
                Ulangi password
              </label>
              <input
                id="confirm"
                type="password"
                className="input"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button type="submit" className="btn-primary w-full" disabled={setup.isPending}>
              {setup.isPending && <Spinner className="text-white" />}
              Buat akun
            </button>
          </form>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
